import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhone, FaWhatsapp, FaCalendarAlt, FaPaw } from 'react-icons/fa';
import IconWrapper from './IconWrapper';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Ana Sayfa', href: '/' },
    { name: 'Hizmetler', href: '/hizmetler' },
    { name: 'Hakkımızda', href: '/hakkimizda' },
    { name: 'İletişim', href: '/iletisim' },
  ];

  const headerClass = `fixed w-full z-50 transition-all duration-300 ${
    isScrolled ? 'py-2 glass shadow-soft' : 'py-4 bg-transparent'
  }`;

  const menuVariants = {
    closed: {
      opacity: 0,
      x: '100%',
      transition: {
        type: 'tween',
        duration: 0.3
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'tween',
        duration: 0.3
      }
    }
  };

  return (
    <header className={headerClass}>
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="bg-gradient-to-br from-primary to-secondary p-2.5 rounded-xl shadow-lg transform group-hover:scale-105 transition-all duration-300">
              <FaPaw className="text-2xl text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-800">Yahşi</span>
              <span className="text-sm text-gray-600 -mt-1">Veteriner Kliniği</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative font-medium text-sm transition-colors duration-200 ${
                  location.pathname === link.href
                    ? 'text-primary'
                    : 'text-gray-700 hover:text-primary'
                }`}
              >
                {link.name}
                {location.pathname === link.href && (
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary"
                    layoutId="underline"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Quick Contact - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+905533077616"
              className="btn btn-primary flex items-center px-4 py-2 rounded-full text-white hover:bg-primary-dark transition-colors duration-200"
            >
              <IconWrapper icon={FaPhone} className="mr-2" />
              <span>0553 307 76 16</span>
            </a>
            <Link
              to="/randevu"
              className="btn btn-secondary flex items-center px-4 py-2 rounded-full text-white hover:bg-secondary-dark transition-colors duration-200"
            >
              <FaCalendarAlt className="mr-2" />
              <span>Randevu Al</span>
            </Link>
            <a
              href="https://wa.me/905533077616"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-whatsapp flex items-center px-4 py-2 rounded-full text-white bg-green-500 hover:bg-green-600 transition-colors duration-200"
            >
              <IconWrapper icon={FaWhatsapp} className="mr-2" />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-lg md:hidden"
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="p-6">
                <div className="flex justify-end mb-8">
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="space-y-6">
                  {navigation.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`block text-lg font-medium ${
                        location.pathname === link.href
                          ? 'text-primary'
                          : 'text-gray-700 hover:text-primary'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <div className="pt-6 space-y-4">
                    <a
                      href="tel:+905533077616"
                      className="btn btn-primary w-full justify-center flex items-center px-4 py-2 rounded-full text-white hover:bg-primary-dark transition-colors duration-200"
                    >
                      <IconWrapper icon={FaPhone} className="mr-2" />
                      <span>0553 307 76 16</span>
                    </a>
                    <Link
                      to="/randevu"
                      className="btn btn-secondary w-full justify-center flex items-center px-4 py-2 rounded-full text-white hover:bg-secondary-dark transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FaCalendarAlt className="mr-2" />
                      <span>Randevu Al</span>
                    </Link>
                    <a
                      href="https://wa.me/905533077616"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-whatsapp w-full justify-center flex items-center px-4 py-2 rounded-full text-white bg-green-500 hover:bg-green-600 transition-colors duration-200"
                    >
                      <IconWrapper icon={FaWhatsapp} className="mr-2" />
                      <span>WhatsApp ile İletişim</span>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header; 